import type { ReactNode } from 'react';
import styles from './CategorySection.module.css';

export type CategoryShade = 'core' | 'functions' | 'advanced' | 'tools' | 'info' | 'plain';

interface Props {
  /** Category header label, e.g. 'Core', 'Functions', 'Advanced'. */
  label: string;
  /** Shade variant — each tints the box subtly so the user can feel
   *  the boundary between Core / Functions / Advanced without leaning
   *  on dividers alone. Hover state brightens the same hue. */
  shade?: CategoryShade;
  /** When provided, the section header becomes a click target that
   *  toggles a chevron in the top-right corner; children render only
   *  when expanded. */
  collapsible?: {
    expanded: boolean;
    onToggle: () => void;
  };
  children: ReactNode;
}

/**
 * Sidebar / RightNav category container — a bounded "floating glass
 * pane" with:
 *  - distinct background shade per shade variant
 *  - 1px tinted hairline border
 *  - inner highlight + outer shadow → glass-pane feel at rest
 *  - hover state that lifts the pane and brightens the border
 *  - centered all-caps mono header label
 *  - absolutely-positioned chevron in the top-right corner for
 *    collapsible sections (chevron sits *outside* the label flow,
 *    so the label stays centered even at narrow rail widths)
 */
export default function CategorySection({
  label,
  shade = 'plain',
  collapsible,
  children,
}: Props) {
  const expanded = collapsible ? collapsible.expanded : true;
  return (
    <section
      className={`${styles.section} ${styles[`shade_${shade}`]} ${expanded ? '' : styles.collapsed}`}
      aria-label={label}
    >
      {collapsible ? (
        <button
          type="button"
          className={styles.headerBtn}
          onClick={collapsible.onToggle}
          aria-expanded={collapsible.expanded}
          aria-label={`${label} — ${collapsible.expanded ? 'collapse' : 'expand'}`}
        >
          <span className={styles.label}>{label}</span>
        </button>
      ) : (
        <div className={styles.headerStatic}>
          <span className={styles.label}>{label}</span>
        </div>
      )}
      {collapsible ? (
        <span
          className={`${styles.chevron} ${collapsible.expanded ? styles.chevronOpen : ''}`}
          aria-hidden="true"
        >
          ▸
        </span>
      ) : null}
      {expanded ? <div className={styles.body}>{children}</div> : null}
    </section>
  );
}
